<template>
  <div class="vendas-servicos">
    <box :title="t('Venda por serviço e prioridade')">
      <div class="vs-filtros" data-testid="vendas-servicos-filtros">
        <input-date elem-id="vs-data-inicio" data-testid="vs-data-inicio" :label="t('Data inicial')" v-model="dataInicio" />
        <input-date elem-id="vs-data-fim" data-testid="vs-data-fim" :label="t('Data final')" v-model="dataFim" />
        <select-list elem-id="vs-prioridade"
                     data-testid="vs-prioridade"
                     :label="t('Prioridade')"
                     :placeholder="t('Todas')"
                     :itens-select="prioridades"
                     :auto-select-first="false"
                     v-model="prioridadeId" />
        <Button size="small" data-testid="vs-buscar" :label="t('Buscar')" @click="buscar" />
      </div>

      <div v-if="erro" class="vs-aviso">{{ erro }}</div>

      <div v-else-if="!carregando && itens.length === 0" class="vs-aviso" data-testid="vendas-servicos-vazio">
        {{ t('Nenhum item de serviço no período e nas franquias do filtro.') }}
      </div>

      <template v-else-if="itens.length > 0">
        <h4>{{ t('Totais por serviço e prioridade') }}</h4>
        <data-table test-id-prefix="vendas-servicos-kpis"
                    :config="kpisConfig"
                    :data="kpis"
                    :endpoint="null"
                    :filterConfig="filterConfig" />

        <h4>{{ t('Itens de OS') }}</h4>
        <data-table test-id-prefix="vendas-servicos-itens"
                    :config="itensConfig"
                    :data="itens"
                    :endpoint="null"
                    :filterConfig="filterConfig" />
      </template>
    </box>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useVendasServicos } from '../../composables/useVendasServicos';

const { t } = useI18n();
const { itens, kpis, prioridades, carregando, erro, carregar, carregarPrioridades } = useVendasServicos();

/** Date-only local, sem UTC: evita o bug de -1 dia em GMT-3. */
function dataLocal(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${dia}`;
}

const hoje = new Date();
const dataInicio = ref<string>(dataLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)));
const dataFim = ref<string>(dataLocal(hoje));
const prioridadeId = ref<string | number | undefined>(undefined);

function buscar(): void {
  const prioridade = prioridadeId.value ? Number(prioridadeId.value) : null;
  void carregar(dataInicio.value, dataFim.value, prioridade);
}

const kpisConfig = computed(() => ({
  tableId: 'tabelaVendasServicosKpis',
  columns: [
    { data: 'servico_nome', title: t('Serviço'), width: 220, sortable: true },
    { data: 'prioridade_nome', title: t('Prioridade'), width: 160, sortable: true },
    { data: 'quantidade', title: t('Quantidade'), width: 110, sortable: true, footerType: 'sum' },
    { data: 'itens', title: t('Itens'), width: 90, sortable: true, footerType: 'sum' },
  ],
  pageLength: 100,
  pagination: true,
  footerLabel: t('Totais'),
  stripedRows: true,
  showGridlines: true,
  size: 'small',
}));

const itensConfig = computed(() => ({
  tableId: 'tabelaVendasServicosItens',
  columns: [
    { data: 'data', title: t('Data'), width: 110, sortable: true },
    { data: 'os_id', title: t('OS'), width: 90, sortable: true },
    { data: 'franquia_nome', title: t('Franquia'), width: 180, sortable: true },
    { data: 'servico_nome', title: t('Serviço'), width: 220, sortable: true },
    { data: 'prioridade_nome', title: t('Prioridade'), width: 160, sortable: true },
    { data: 'quantidade', title: t('Quantidade'), width: 110, sortable: true, footerType: 'sum' },
  ],
  pageLength: 100,
  pagination: true,
  footerLabel: t('Totais'),
  resizableColumns: true,
  stripedRows: true,
  showGridlines: true,
  size: 'small',
}));

const filterConfig = computed(() => ({
  searchName: t('Procurar'),
  choosePlaceholder: t('Escolha'),
}));

onMounted(() => {
  log('VendasServicosPage', 'mounted', 'Component mounted');
  void carregarPrioridades();
  buscar();
  // As franquias vêm do filtro global: trocou o filtro, recarrega.
  eventBus.on('filter.updated', buscar);
});

onBeforeUnmount(() => {
  eventBus.off('filter.updated', buscar);
});

defineExpose({ loadData: buscar });
</script>

<style scoped>
.vs-filtros {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.vs-aviso {
  padding: 1rem;
}
</style>
